import React from 'react';
import { connect } from 'react-redux';

import ScrollEvent from '../../shared/components/scroll-event';
import withWindowResize from '../../ui/components/window-resize';
import SearchSort from '../../search-filter/components/search-sort';
import Dropdown from '../../ui/components/dropdown';
import { Helpers } from '../../core/src/helpers';
import { withError } from '../../shared/components/error-boundary';

import '../css/dlp-sort-dropdown.less';

const SORT_OPTIONS = [
    { label: 'Best seller', value:0, field: 'organic', order: 'organic' },
    { label: 'Lowest price', value: 1,field: 'sale_price', order: 'asc' },
    { label: 'Highest price', value: 2, field: 'sale_price', order: 'desc' },
    { label: 'Name', value: 3, field: 'name', order: 'asc' },
    { label: 'Rating', value: 4, field: 'rating', order: 'desc' }
];

const camelCase = (text) => 
    text.split('_').map((word, index)=> {
        if(index === 0) return word;
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }).join('')

class DLPSortDropDown extends React.PureComponent{

    /**
     * Sorts results array offline by pricing info contained in the prices array in redux store.
     */
    sortResultsByPricingInfo = Helpers.memoize((newSort, results, prices, searchKey, pageSize) => {
        // flatten pages into a single array of results
        const allResults = results;
        // for quick lookups
        const hashMaps = {
            results: allResults.reduce((prev, curr) => {
                prev[curr.sku] = curr;
                return prev;
            }, {}),
            prices
        };

        // for sorting and guaranteed order
        const orderedLists = {
            results: allResults,
            prices:  allResults.reduce( (pricesArray, p)  => {
               let priceObj =  prices[p.sku];
               if(!priceObj) return pricesArray; //if no price for that sku, just filter them out eventually and put to bottom of sorted results.
               priceObj.sku = p.sku;
               pricesArray.push(priceObj);
               return pricesArray;
            }, [])


        };

        // lodash format
        const parsedSort = camelCase(newSort.field);

        // sort prices list
       let defaultPriceField = 'listPrice'; // in case it doesn't have a sale price.
        const sortedPrices = orderedLists.prices.sort((a,b) => (
            (typeof a[parsedSort] === 'number' ? a[parsedSort] : a[defaultPriceField]) - 
            (typeof b[parsedSort] === 'number' ? b[parsedSort] : b[defaultPriceField])
        )); 
        // lodash.sortBy sorts by 'asc' order only
        if (newSort.order === 'desc') {
            sortedPrices.reverse();
        }

        // map ordering to the products array (time efficiently)
        const sortedResults = [];

        for (let price of sortedPrices) {
            sortedResults.push(hashMaps.results[price.sku]);
        }

        // sometimes HPServices does not return pricing info for some SKUs.
        // this usually means that they went inactive (intraday, assuming daily Swiftype reindexing lambda job works).
        // add them to the bottom of the list to avoid modifying size / deleting elements from the results list:
        const inactiveResults = [];
        for (let result of orderedLists.results) {
            if (!hashMaps.prices[result.sku]) {
                inactiveResults.push(result);
            }
        }
        const finalList = sortedResults.concat(inactiveResults);
        return finalList;
    },
        (newSort, results, prices, searchKey, pageSize) => `${newSort.field}-${newSort.order}-${results.length}-${prices.length}-${searchKey}`
    );

    /**
     * Sort results array offline with JavaScript
     */
    sortResults = Helpers.memoize((newSort, results, saveOrganic, searchKey, pageSize) => {
        // will just return the results to save their order in the memoize cache
        if (saveOrganic && typeof saveOrganic === 'boolean') {
            return results;
        }
        // flatten pages into a single array
        const allResults = results.slice();
        // lodash format
        const parsedSort = camelCase(newSort.field);
        // sort array
        const sorted = allResults.sort((a,b) => {
            let comp1 = typeof a[parsedSort] === 'string'
                        ? a[parsedSort].toLowerCase()
                        : a[parsedSort];
            let comp2 = typeof b[parsedSort] === 'string'
                        ? b[parsedSort].toLowerCase()
                        : b[parsedSort];

            return comp1 < comp2
                    ? -1
                    : comp1 > comp2
                    ? 1
                    : 0;
        });
        // lodash.sortBy sorts by 'asc' order only
        if (newSort.order === 'desc') {
            sorted.reverse();
        }

        return sorted;
    },
        (newSort, results, saveOrganic, searchKey, pageSize) => `${newSort.field}-${newSort.order}-${results.length}-${saveOrganic}-${searchKey}`
    );

    offlineSort = (searchKey, results, newSort, pageSize) => {
        const { prices } = this.props;
        const isPriceSort = newSort.field.toLowerCase().endsWith('price');
        const sortingFn = isPriceSort
            ? this.sortResultsByPricingInfo
            : this.sortResults;
        return sortingFn.apply(this, [newSort, results, isPriceSort ? prices : false, searchKey, pageSize]);
    }

    sortChange = (setSort, options) => (value) => {
        const { field, order } = options[value] || {};
        setSort({ field, order }, this.offlineSort);
    }

    render(){
        const { results, customBreakpoints } = this.props;

        let resultsCountComp = <div className="dlp-results-number"><strong>{results && results.length}</strong> RESULTS</div>;
        let isMobile = customBreakpoints && customBreakpoints['dlp-v2'] === 'mobile';
        let sortComp = (
            <SearchSort options={SORT_OPTIONS}>
                {
                    ({ options, value, setSort }) => {
                        return (<Dropdown 
                            options={options} 
                            onChange={this.sortChange(setSort, options)} 
                            label={'Sort by'} 
                            value={value} />)
                    }
                }
            </SearchSort>
        );

        return (
            <div className="dlp-sort-results">
                    {
                        isMobile
                        ? <ScrollEvent botBound=".dlp-sort-container" botScrollOffset={900}>
                            {
                                (attachRef, scrollState) => {
                                    const { botReached, isSticky } = scrollState || {};

                                    return <div className="dlp-sort-container" ref={attachRef}>
                                        {
                                            botReached
                                            ? <React.Fragment><div className="dlp-mobile-sort">{sortComp}</div>{resultsCountComp}</React.Fragment>
                                            : <React.Fragment>{sortComp}{resultsCountComp}</React.Fragment>
                                        }
                                    </div>
                                }
                            }
                            </ScrollEvent>
                        : <React.Fragment>{resultsCountComp}{sortComp}</React.Fragment>
                    }
            </div>
        );
    }
}

export const mapStateToProps = state => {
    const { slugInfo, productData } = state || {};
    const { components } = slugInfo || {};
    const { dlpBanner } = components || {};
    const { intelBanner=false } = dlpBanner || {};

    return {
        intelBanner,
        prices: productData.productInfo.productPrices
    }
};

export default connect(mapStateToProps)(withError(withWindowResize(DLPSortDropDown)));
